import * as React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { ReportPeriod } from "@/types/reports";
import { formatDateByPeriod, getXAxisLabel } from "@/lib/reports/period-utils";

export interface ReportBarChartProps {
  data: Array<Record<string, unknown>>;
  dataKeys: Array<{
    key: string;
    label: string;
    color?: string;
  }>;
  xAxisKey: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  stacked?: boolean;
  showLegend?: boolean;
  showGrid?: boolean;
  className?: string;
  formatValue?: (value: number) => string;
  formatLabel?: (value: string) => string;
  period?: ReportPeriod;
  onBarClick?: (data: Record<string, unknown>, index: number) => void;
}

export function ReportBarChart({
  data,
  dataKeys,
  xAxisKey,
  xAxisLabel,
  yAxisLabel,
  height = 350,
  stacked = false,
  showLegend = true,
  showGrid = true,
  className,
  formatValue,
  formatLabel,
  period,
  onBarClick,
}: ReportBarChartProps) {
  const chartConfig = React.useMemo(() => {
    const config: ChartConfig = {};
    dataKeys.forEach((item) => {
      config[item.key] = {
        label: item.label,
        // Use theme object for light/dark mode colors
        theme: {
          light: item.color || "hsl(var(--chart-1))",
          dark: item.color ? item.color.replace(")", " / 0.8)") : "hsl(var(--chart-1) / 0.8)",
        },
      };
    });
    return config;
  }, [dataKeys]);

  // Auto-configure labels and formatters based on period prop
  const effectiveXAxisLabel = React.useMemo(() => {
    if (xAxisLabel !== undefined) {
      return xAxisLabel;
    }
    return period ? getXAxisLabel(period) : undefined;
  }, [xAxisLabel, period]);

  const effectiveFormatLabel = React.useMemo(() => {
    if (formatLabel !== undefined) {
      return formatLabel;
    }
    return period
      ? (value: string) => formatDateByPeriod(value, period)
      : undefined;
  }, [formatLabel, period]);

  return (
    <div className={cn("w-full overflow-hidden", className)} style={{ height, minHeight: height }}>
      <ChartContainer config={chartConfig} className="h-full w-full">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          barGap={8}
          barCategoryGap="20%"
          accessibilityLayer
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" vertical={false} />}
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            label={effectiveXAxisLabel ? { value: effectiveXAxisLabel, position: "insideBottom", offset: -10 } : undefined}
            tickFormatter={effectiveFormatLabel}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: "insideLeft" } : undefined}
            tickFormatter={formatValue}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          {showLegend && <ChartLegend content={<ChartLegendContent />} />}
          {dataKeys.map((item) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              fill={item.color || `var(--color-${item.key})`}
              radius={[4, 4, 0, 0]}
              stackId={stacked ? "stack" : undefined}
              onClick={onBarClick ? (_, index) => onBarClick(data[index], index) : undefined}
              cursor={onBarClick ? "pointer" : "default"}
              style={onBarClick ? { transition: 'opacity 0.2s' } : undefined}
              onMouseEnter={onBarClick ? (e) => e.target.style.opacity = '0.8' : undefined}
              onMouseLeave={onBarClick ? (e) => e.target.style.opacity = '1' : undefined}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </div>
  );
}
